import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeTwoToneIcon from "@mui/icons-material/HomeTwoTone";
import PersonSearchTwoToneIcon from "@mui/icons-material/PersonSearchTwoTone";
import WorkTwoToneIcon from "@mui/icons-material/WorkTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import CalendarTodayTwoToneIcon from "@mui/icons-material/CalendarTodayTwoTone";
import KeyboardDoubleArrowRightSharpIcon from "@mui/icons-material/KeyboardDoubleArrowRightSharp";
import KeyboardDoubleArrowLeftSharpIcon from "@mui/icons-material/KeyboardDoubleArrowLeftSharp";
import { Collapse, Tooltip, Stack } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import MuiAppBar from "@mui/material/AppBar";
import { tooltipClasses } from "@mui/material/Tooltip";
import LogoutIcon from "@mui/icons-material/Logout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../Redux/slices/authSlice";
import { CapitalizeName } from "../../../utils/capitalizeName";
import HRcandidate from "../HRcandidate";
import HRTable from "../HRcandidate/HRTable";
import createInstance from "../../../api/axiosinstance";
import JobDescription from "../../organization/JobDescription";
import JobDescriptionManagementList from "../../organization/JobDescriptionManagementList";
import ScheduleInterview from "../ScheduleInterview";
import InterviewsTable from "../InterviewsTable";
import ZoomMeeting from "../Zoom";

const drawerWidth = 300;

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  borderTopRightRadius: 30,
  borderBottomRightRadius: 30,
  backgroundColor: "#006A71",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderTopRightRadius: 30,
  borderBottomRightRadius: 30,
  backgroundColor: "#006A71",
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  backgroundColor: "#1E1E1E",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  borderRadius: 10,
  backgroundColor: "#1E1E1E",
  marginTop: 15,
  marginRight: 20,
  width: `calc(100% - 7%)`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth + 40}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const HRLandingPage = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [menudata, setMenudata] = useState("Dashboard");
  const [titleText, setTitleText] = useState("HR Dashboard");
  const dispatch = useDispatch();
  const userName = CapitalizeName(user?.name || "");
  const navigate = useNavigate();
  const aiDisha = createInstance("DISHA");
  const organizationId = user?.organizationId;
  const [showCreateNewJob, setShowCreateNewJob] = useState(false);
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);

  useEffect(() => {
    Dashboard();
  }, []);

  const handleOpenCreateNewJob = () => {
    setMenudata("");
    setTitleText("Create New Job");
    setShowCreateNewJob(true);
    setShowScheduleInterview(false);
  };

  const handleOpenScheduleInterview = () => {
    setMenudata("");
    setTitleText("Schedule Interview");
    setShowScheduleInterview(true);
    setShowCreateNewJob(false);
  };

  const handleCloseScheduleInterview = () => {
    setShowScheduleInterview(false);
    setMenudata("Dashboard");
    setTitleText("HR Dashboard");
  };

  const Dashboard = async () => {
    try {
      const response = await aiDisha.get(
        `/hr_dashboard?organization_id=${organizationId}`
      );
      console.log("dashboard", response.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  const icons = [
    HomeTwoToneIcon,
    PersonSearchTwoToneIcon,
    WorkTwoToneIcon,
    EventTwoToneIcon,
    CalendarTodayTwoToneIcon, // Added for Interviews
  ];

  const handleListItemClick = (item) => {
    setMenudata(item);
    setTitleText(item === "Dashboard" ? "HR Dashboard" : item);
    setShowCreateNewJob(false);
    setShowScheduleInterview(false);
  };

  const [open, setOpen] = useState(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const [openIndex, setOpenIndex] = useState(null);
  const handleItemClick = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleLogOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logout());
    setTimeout(() => navigate("/"), 0);
  };

  if (!isAuthenticated || user?.role !== "HR") {
    navigate("/");
    return null;
  }

  return (
    <Box sx={{ display: "flex", backgroundColor: "white", height: "100vh" }}>
      <AppBar position="fixed" open={open} sx={{ background: "#006A71" }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              color="white"
              sx={{ fontSize: "25px", textAlign: "left" }}
            >
              {titleText}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ position: "relative" }}>
          <Box
            sx={{
              backgroundColor: "white",
              width: "100%",
              height: "77%",
              marginTop: "5%",
              marginRight: "30px",
              borderRadius: "10px 30px 0px 20px",
              position: "relative",
            }}
          >
            {isAuthenticated && (
              <LightTooltip title={`${user?.name || ""}`} placement="right">
                <Typography
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "20px",
                    transform: "translateY(-50%)",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#1E1E1E",
                    maxWidth: "160px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: open ? "block" : "none",
                  }}
                >
                  Welcome {userName}
                </Typography>
              </LightTooltip>
            )}
          </Box>
        </DrawerHeader>
        <DrawerHeader>
          <IconButton
            color="white"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              color: "white",
              marginRight: 1,
              marginLeft: 1,
            }}
          >
            {open ? (
              <KeyboardDoubleArrowLeftSharpIcon />
            ) : (
              <KeyboardDoubleArrowRightSharpIcon />
            )}
          </IconButton>
        </DrawerHeader>

        <List sx={{ color: "white" }}>
          {[
            "Dashboard",
            "Candidate FeedBack",
            "Manage Jobs",
            "Schedule Interview",
            "Interviews",
            "Zoom Records"
          ].map((text, index) => (
            <React.Fragment key={text}>
              <ListItem
                disablePadding
                onClick={() => {
                  if (text === "Schedule Interview") {
                    handleOpenScheduleInterview();
                  } else {
                    handleListItemClick(text);
                  }
                }}
              >
                <ListItemButton onClick={() => handleItemClick(index)}>
                  <ListItemIcon sx={{ color: "white" }}>
                    <LightTooltip title={text}>
                      {React.createElement(icons[index % icons.length])}
                    </LightTooltip>
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
        <DrawerHeader>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              color: "white",
              marginRight: 1,
            }}
          >
            <Stack direction="column" spacing={1}>
              {open ? (
                <KeyboardDoubleArrowLeftSharpIcon />
              ) : (
                <KeyboardDoubleArrowRightSharpIcon />
              )}
              {isAuthenticated && (
                <div onClick={handleLogOut} title="Logout">
                  <LogoutIcon />
                </div>
              )}
            </Stack>
          </IconButton>
        </DrawerHeader>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minHeight: "100vh",
          backgroundColor: "white",
          overflow: "auto",
        }}
      >
        <DrawerHeader />
        {menudata === "Dashboard" && <HRcandidate />}
        {menudata === "Candidate FeedBack" && <HRTable />}
        {menudata === "Manage Jobs" && (
          <JobDescriptionManagementList
            onOpenJobDescription={handleOpenCreateNewJob}
          />
        )}
        {showCreateNewJob && <JobDescription />}
        {showScheduleInterview && (
          <ScheduleInterview onClose={handleCloseScheduleInterview} />
        )}
        {menudata === "Interviews" && <InterviewsTable />}
        {menudata === "Zoom Records" && <ZoomMeeting />}
      </Box>
    </Box>
  );
};

export default HRLandingPage;
